import { Handle, Position } from '@xyflow/react';
import { type StyleDeclarationValue, css } from 'aphrodite';
import { type ReactNode } from 'react';
import { type BaseNodeData } from '../types';
import { nodeStyles } from './styles';

interface BaseNodeProps {
	data: BaseNodeData;
	selected?: boolean;
	icon?: ReactNode;
	containerStyle?: StyleDeclarationValue;
	showSourceHandle?: boolean;
	showTargetHandle?: boolean;
	targetPosition?: Position;
	sourcePosition?: Position;
}

export function BaseNode({
	data,
	selected,
	icon,
	containerStyle,
	showSourceHandle = true,
	showTargetHandle = true,
	targetPosition = Position.Top,
	sourcePosition = Position.Bottom,
}: BaseNodeProps) {
	const styles = [nodeStyles.baseContainer];
	if (selected) {
		styles.push(nodeStyles.selected);
	}
	if (containerStyle) {
		styles.push(containerStyle);
	}

	const outputHandles = data.outputHandles || ['default'];
	const hasMultipleOutputs = outputHandles.length > 1;

	const isHorizontal = sourcePosition === Position.Left || sourcePosition === Position.Right;

	const calculateHandleStyle = (index: number, total: number) => {
		if (total === 1) {
			return isHorizontal ? { top: '50%' } : { left: '50%' };
		}

		const percentage = (100 / (total + 1)) * (index + 1);
		return isHorizontal ? { top: `${percentage}%` } : { left: `${percentage}%` };
	};

	return (
		<div className={css(...styles)}>
			{showTargetHandle && <Handle type='target' position={targetPosition} id='target' />}

			<div className={css(nodeStyles.header)}>
				{icon}
				<span>{data.label}</span>
			</div>

			{data.summary && <div className={css(nodeStyles.summary)}>{data.summary}</div>}

			{showSourceHandle &&
				outputHandles.map((handleId, index) => (
					<Handle
						key={handleId}
						type='source'
						position={sourcePosition}
						id={handleId}
						style={hasMultipleOutputs ? calculateHandleStyle(index, outputHandles.length) : undefined}
					/>
				))}
		</div>
	);
}
